import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MapIcon from '@mui/icons-material/Map';
import MainLayout from './layout/MainLayout';
import api from '../services/api';
import { getCategoryColor } from './map/categoryPalette';
import { brand } from '../theme';

// Public-facing vendor directory. Powered by the same /api/map endpoint the
// festival map uses, so the list always reflects who has actually registered
// a booth this year. Designed for an older audience: big search box, large
// alphabetical headings, generous row spacing, one clear "Show on Map" CTA
// per vendor.
const VendorsList = () => {
  const navigate = useNavigate();
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/map')
      .then((res) => {
        if (cancelled) return;
        setCells(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load vendor directory:', err);
        setError('We could not load the vendor list. Please try again later.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reduce the map cells down to one entry per registered vendor. A vendor
  // can hold multiple booths; we list them once with all booth numbers.
  const vendors = useMemo(() => {
    const byVendor = new Map();
    for (const cell of cells) {
      if (!cell.is_registered || !cell.vendor_name) continue;
      const key = String(cell.vendor_name).trim().toLowerCase();
      if (!byVendor.has(key)) {
        byVendor.set(key, {
          name: cell.vendor_name,
          categoryName: cell.category_name || 'Other',
          categorySlug: cell.category_slug || 'other',
          booths: [],
          firstBoothId: cell.id,
        });
      }
      const entry = byVendor.get(key);
      if (cell.cell_name) entry.booths.push(cell.cell_name);
      // Prefer the smallest booth id as the deep-link target so "Show on
      // Map" lands on a stable booth even if a vendor has several.
      if (cell.id < entry.firstBoothId) entry.firstBoothId = cell.id;
    }

    return Array.from(byVendor.values())
      .map((v) => ({
        ...v,
        booths: Array.from(new Set(v.booths)).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [cells]);

  const categories = useMemo(() => {
    const seen = new Map();
    for (const v of vendors) {
      if (!seen.has(v.categorySlug)) {
        seen.set(v.categorySlug, { slug: v.categorySlug, name: v.categoryName });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [vendors]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vendors.filter((v) => {
      if (activeCategory && v.categorySlug !== activeCategory) return false;
      if (!q) return true;
      if (v.name.toLowerCase().includes(q)) return true;
      if (v.categoryName.toLowerCase().includes(q)) return true;
      if (v.booths.some((b) => String(b).toLowerCase().includes(q))) return true;
      return false;
    });
  }, [vendors, query, activeCategory]);

  // Group filtered results A-Z so older users can skim sections.
  const grouped = useMemo(() => {
    const groups = new Map();
    for (const v of filtered) {
      const first = v.name.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(first) ? first : '#';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(v);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const handleShowOnMap = (boothId) => {
    navigate(`/map?booth=${boothId}`);
  };

  return (
    <MainLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography variant="h1" sx={{ color: brand.navy, mb: 1.5, textAlign: 'center' }}>
          This Year's Vendors
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            color: brand.textSecondary,
            fontSize: '1.1875rem',
            maxWidth: 560,
            mx: 'auto',
            mb: { xs: 4, sm: 5 },
          }}
        >
          Search for a vendor by name, category, or booth number. Tap "Show on
          Map" to see exactly where they'll be.
        </Typography>

        <TextField
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search vendors or booth number"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: brand.textSecondary, fontSize: 28 }} />
              </InputAdornment>
            ),
            sx: {
              fontSize: '1.125rem',
              backgroundColor: brand.paper,
            },
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              minHeight: 64,
            },
          }}
        />

        {categories.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mb: 4,
            }}
          >
            <Chip
              label="All categories"
              onClick={() => setActiveCategory(null)}
              variant={activeCategory === null ? 'filled' : 'outlined'}
              sx={{
                fontSize: '0.95rem',
                height: 36,
                backgroundColor: activeCategory === null ? brand.navy : 'transparent',
                color: activeCategory === null ? '#fff' : brand.navy,
                borderColor: brand.navy,
                '&:hover': {
                  backgroundColor: activeCategory === null ? brand.navyHover : 'rgba(16, 36, 62, 0.04)',
                },
              }}
            />
            {categories.map((cat) => {
              const isActive = activeCategory === cat.slug;
              const color = getCategoryColor(cat.slug);
              return (
                <Chip
                  key={cat.slug}
                  label={cat.name}
                  onClick={() => setActiveCategory(isActive ? null : cat.slug)}
                  variant={isActive ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.95rem',
                    height: 36,
                    borderColor: color,
                    color: isActive ? '#fff' : brand.textPrimary,
                    backgroundColor: isActive ? color : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive ? color : `${color}1A`,
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && filtered.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              textAlign: 'center',
              border: `1px solid ${brand.hairline}`,
              backgroundColor: brand.cream,
            }}
          >
            <StorefrontIcon sx={{ fontSize: 40, color: brand.textSecondary, mb: 1 }} />
            <Typography variant="h4" sx={{ color: brand.navy, mb: 1 }}>
              No vendors match that search
            </Typography>
            <Typography sx={{ color: brand.textSecondary }}>
              Try a different name, category, or booth number.
            </Typography>
          </Paper>
        )}

        {!loading && !error && grouped.length > 0 && (
          <Box>
            {grouped.map(([letter, items]) => (
              <Box key={letter} sx={{ mb: 4 }}>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: '1.75rem',
                    color: brand.brass,
                    borderBottom: `1px solid ${brand.hairline}`,
                    pb: 1,
                    mb: 2,
                  }}
                >
                  {letter}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {items.map((v) => (
                    <VendorRow
                      key={v.name}
                      vendor={v}
                      onShowOnMap={() => handleShowOnMap(v.firstBoothId)}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </MainLayout>
  );
};

const VendorRow = ({ vendor, onShowOnMap }) => {
  const swatch = getCategoryColor(vendor.categorySlug);
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 3 },
        p: { xs: 2.5, sm: 3 },
        border: `1px solid ${brand.hairline}`,
        backgroundColor: brand.paper,
        transition: 'border-color 200ms ease-out, box-shadow 200ms ease-out',
        '&:hover': {
          borderColor: brand.navy,
          boxShadow: '0 6px 18px rgba(16, 36, 62, 0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 6,
            alignSelf: 'stretch',
            backgroundColor: swatch,
            borderRadius: 1,
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: { xs: '1.25rem', sm: '1.375rem' },
              color: brand.navy,
              lineHeight: 1.25,
            }}
          >
            {vendor.name}
          </Typography>
          <Typography sx={{ color: brand.textSecondary, fontSize: '1rem', mt: 0.25 }}>
            {vendor.categoryName}
            {vendor.booths.length > 0 && (
              <>
                {' '}
                &middot; Booth{vendor.booths.length > 1 ? 's' : ''} {vendor.booths.join(', ')}
              </>
            )}
          </Typography>
        </Box>
      </Box>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        startIcon={<MapIcon />}
        onClick={onShowOnMap}
        sx={{
          alignSelf: { xs: 'stretch', sm: 'center' },
          minWidth: { sm: 180 },
          py: 1.25,
        }}
      >
        Show on Map
      </Button>
    </Paper>
  );
};

export default VendorsList;
